import type {
  Condition,
  ConditionLiteral,
  NumberLiteral,
  ObservationOperand,
  Operand,
  Operator,
} from '#src/observation-logic/conditionSchema.ts'
import { conditionSchema } from '#src/observation-logic/conditionSchema.ts'

// Names an author can write after `#` for an observation, such as
// `fh_cancer`, and the code each stands for.
export type ConditionSymbols = Readonly<
  Record<string, { code: string; system?: string }>
>

export interface ConditionParseError {
  message: string
  // Zero-based offset into the text.
  position: number
  // One-based, as an editor shows it.
  column: number
}

export type ConditionParseResult =
  | { ok: true; condition: Condition }
  | { ok: false; error: ConditionParseError }

type TokenType =
  | 'hash'
  | 'ident'
  | 'number'
  | 'date'
  | 'string'
  | 'op'
  | '('
  | ')'
  | '['
  | ']'
  | ','
  | 'end'

interface Token {
  type: TokenType
  text: string
  position: number
  // A number's unit, or a hash operand's scope.
  suffix?: string
}

const KEYWORDS = new Set([
  'and',
  'or',
  'not',
  'is',
  'answered',
  'in',
  'true',
  'false',
])
const IDENT_START = /[A-Za-z_]/
const IDENT_PART = /[A-Za-z0-9_.-]/
const CODE_PART = /[A-Za-z0-9_.-]/
const DATE = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2})?)?/
const NUMBER = /^-?\d+(\.\d+)?/
interface OperatorTexts {
  readonly [text: string]: Operator
}

const OPERATOR_TEXT: OperatorTexts = {
  '=': '=',
  '!=': '!=',
  '≠': '!=',
  '<': '<',
  '<=': '<=',
  '≤': '<=',
  '>': '>',
  '>=': '>=',
  '≥': '>=',
}

class Failure extends Error {
  position: number
  constructor(message: string, position: number) {
    super(message)
    this.position = position
  }
}

function fail(message: string, position: number): never {
  throw new Failure(message, position)
}

function tokenize(text: string): Token[] {
  const tokens: Token[] = []
  let index = 0
  const readWhile = (pattern: RegExp): string => {
    const start = index
    while (index < text.length && pattern.test(text.charAt(index))) {
      index++
    }
    return text.slice(start, index)
  }
  while (index < text.length) {
    const char = text.charAt(index)
    if (/\s/.test(char)) {
      index++
      continue
    }
    const position = index
    if ('()[],'.includes(char)) {
      tokens.push({ type: char as TokenType, text: char, position })
      index++
      continue
    }
    const two = text.slice(index, index + 2)
    if (OPERATOR_TEXT[two]) {
      tokens.push({ type: 'op', text: two, position })
      index += 2
      continue
    }
    if (OPERATOR_TEXT[char]) {
      tokens.push({ type: 'op', text: char, position })
      index++
      continue
    }
    if (char === '#') {
      index++
      const name = readWhile(CODE_PART)
      if (!name) {
        fail('Expected an observation name or code after #.', position)
      }
      let suffix: string | undefined
      if (text.charAt(index) === '@') {
        index++
        suffix = readWhile(/[a-z]/)
        if (suffix !== 'nearest' && suffix !== 'any') {
          fail('Expected @nearest or @any.', index - suffix.length - 1)
        }
      }
      tokens.push({ type: 'hash', text: name, position, suffix })
      continue
    }
    if (char === '"' || char === "'") {
      index++
      let value = ''
      while (index < text.length && text.charAt(index) !== char) {
        if (text.charAt(index) === '\\' && index + 1 < text.length) {
          index++
        }
        value += text.charAt(index)
        index++
      }
      if (index >= text.length) {
        fail('Unterminated string.', position)
      }
      index++
      tokens.push({ type: 'string', text: value, position })
      continue
    }
    const date = text.slice(index).match(DATE)
    if (date) {
      index += date[0].length
      tokens.push({ type: 'date', text: date[0], position })
      continue
    }
    const number = text.slice(index).match(NUMBER)
    if (number) {
      index += number[0].length
      tokens.push({
        type: 'number',
        text: number[0],
        position,
        suffix: readUnit(),
      })
      continue
    }
    if (IDENT_START.test(char)) {
      tokens.push({ type: 'ident', text: readWhile(IDENT_PART), position })
      continue
    }
    fail(`Unexpected "${char}".`, position)
  }
  tokens.push({ type: 'end', text: '', position: text.length })
  return tokens

  // A UCUM unit after a number, such as `kg`, `mm[Hg]` or `{beats}/min`.
  // Brackets belong to the unit only when it opened them, so `[70 kg]`
  // keeps its closing bracket.
  function readUnit(): string | undefined {
    let lookahead = index
    while (lookahead < text.length && text.charAt(lookahead) === ' ') {
      lookahead++
    }
    let end = lookahead
    let depth = 0
    while (end < text.length) {
      const next = text.charAt(end)
      if (next === '[' || next === '{') {
        depth++
      } else if ((next === ']' || next === '}') && depth > 0) {
        depth--
      } else if (depth === 0 && !/[A-Za-z0-9_/%.*^]/.test(next)) {
        break
      }
      end++
    }
    const unit = text.slice(lookahead, end)
    if (!unit || depth > 0 || KEYWORDS.has(unit) || /^\d/.test(unit)) {
      return undefined
    }
    index = end
    return unit
  }
}

// Parses the condition language: `age >= 75`, `#fh_cancer = yes`,
// `#fh_cancer is answered`, `is not answered`, `and`, `or`, `not`,
// parentheses, `in [a, b]`, dates as YYYY-MM-DD, strings in quotes and
// codes as bare words. `#name` reads an observation, through `symbols`
// when the name is there, else as its code; a bare name reads a variable.
export function parseCondition(
  text: string,
  symbols: ConditionSymbols = {}
): ConditionParseResult {
  let tokens: Token[]
  try {
    tokens = tokenize(text)
  } catch (error) {
    return failure(error)
  }
  let index = 0
  const peek = (): Token => tokens[index] ?? tokens[tokens.length - 1]
  const next = (): Token => {
    const token = peek()
    index++
    return token
  }
  const isWord = (word: string): boolean => {
    const token = peek()
    return token.type === 'ident' && token.text === word
  }
  const expectWord = (word: string): void => {
    if (!isWord(word)) {
      fail(`Expected "${word}".`, peek().position)
    }
    index++
  }

  const parseOr = (): Condition => {
    const conditions = [parseAnd()]
    while (isWord('or')) {
      index++
      conditions.push(parseAnd())
    }
    return conditions.length === 1 ? conditions[0] : { type: 'any', conditions }
  }
  const parseAnd = (): Condition => {
    const conditions = [parseUnary()]
    while (isWord('and')) {
      index++
      conditions.push(parseUnary())
    }
    return conditions.length === 1 ? conditions[0] : { type: 'all', conditions }
  }
  const parseUnary = (): Condition => {
    if (isWord('not')) {
      index++
      return { type: 'not', condition: parseUnary() }
    }
    if (peek().type === '(') {
      index++
      const inner = parseOr()
      if (peek().type !== ')') {
        fail('Expected ")".', peek().position)
      }
      index++
      return inner
    }
    return parseTest()
  }
  const parseOperand = (): Operand => {
    const token = next()
    if (token.type === 'hash') {
      const symbol = symbols[token.text]
      const operand: ObservationOperand = {
        source: 'observation',
        code: symbol?.code ?? token.text,
      }
      if (symbol?.system) {
        operand.system = symbol.system
      }
      if (token.suffix === 'nearest' || token.suffix === 'any') {
        operand.scope = token.suffix
      }
      return operand
    }
    if (token.type === 'ident' && !KEYWORDS.has(token.text)) {
      return { source: 'variable', id: token.text }
    }
    return fail('Expected a variable or an #observation.', token.position)
  }
  const parseLiteral = (): ConditionLiteral => {
    const token = next()
    switch (token.type) {
      case 'number': {
        const literal: NumberLiteral = {
          kind: 'number',
          value: Number(token.text),
        }
        if (token.suffix) {
          literal.unit = token.suffix
        }
        return literal
      }
      case 'date':
        return { kind: 'date', value: token.text }
      case 'string':
        return { kind: 'string', value: token.text }
      case 'ident':
        if (token.text === 'true' || token.text === 'false') {
          return { kind: 'boolean', value: token.text === 'true' }
        }
        if (KEYWORDS.has(token.text)) {
          return fail(`Expected a value, not "${token.text}".`, token.position)
        }
        return { kind: 'coding', code: token.text }
      default:
        return fail('Expected a value.', token.position)
    }
  }
  const parseTest = (): Condition => {
    const start = peek().position
    const operand = parseOperand()
    if (isWord('is')) {
      index++
      const negated = isWord('not')
      if (negated) {
        index++
      }
      expectWord('answered')
      return { type: 'answered', operand, answered: !negated }
    }
    if (isWord('in')) {
      index++
      if (peek().type !== '[') {
        fail('Expected "[" after in.', peek().position)
      }
      index++
      const values = [parseLiteral()]
      while (peek().type === ',') {
        index++
        values.push(parseLiteral())
      }
      if (peek().type !== ']') {
        fail('Expected "]".', peek().position)
      }
      index++
      return checked(
        { type: 'compare', operand, op: 'in', value: values },
        start
      )
    }
    const op = peek()
    if (op.type !== 'op') {
      fail('Expected a comparison, "in" or "is".', op.position)
    }
    index++
    return checked(
      {
        type: 'compare',
        operand,
        op: OPERATOR_TEXT[op.text] ?? '=',
        value: parseLiteral(),
      },
      start
    )
  }

  try {
    if (peek().type === 'end') {
      fail('Expected a condition.', 0)
    }
    const condition = parseOr()
    if (peek().type !== 'end') {
      fail(`Unexpected "${peek().text}".`, peek().position)
    }
    return { ok: true, condition }
  } catch (error) {
    return failure(error)
  }
}

// A comparison the language can write but the model does not allow, such as
// `>` on a code, fails where it starts.
function checked(condition: Condition, position: number): Condition {
  const parsed = conditionSchema.safeParse(condition)
  if (!parsed.success) {
    fail(parsed.error.issues[0]?.message ?? 'Invalid comparison.', position)
  }
  return condition
}

function failure(cause: unknown): ConditionParseResult {
  if (cause instanceof Failure) {
    return {
      ok: false,
      error: {
        message: cause.message,
        position: cause.position,
        column: cause.position + 1,
      },
    }
  }
  throw cause
}
